import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { X } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@core/utils/cn';

import { IconButton } from './IconButton';
import { useTranslation } from '@core/i18n';

/**
 * Centred modal.
 *
 * Anatomy: Root -> Portal -> Backdrop -> Viewport -> Popup.
 *
 * `Viewport` is optional in Base UI but we use it everywhere, for one reason: it is the
 * scroll container. Without it a dialog whose content grows past the viewport clips
 * instead of scrolling, and that only shows up on a small laptop with a long form --
 * i.e. in front of someone else. Adopting it uniformly means no dialog is ever the one
 * that was built the other way.
 *
 * Dialog has no gesture or snap-point support by design. The mobile bottom sheet uses
 * Base UI's Drawer instead -- see Sheet.
 */
export interface DialogProps extends ComponentProps<typeof BaseDialog.Root> {}

export function Dialog(props: DialogProps) {
  return <BaseDialog.Root {...props} />;
}

Dialog.Trigger = BaseDialog.Trigger;
Dialog.Close = BaseDialog.Close;

export interface DialogContentProps {
  children: ReactNode;
  className?: string;
  /** Width cap. Defaults to a comfortable form width. */
  size?: 'sm' | 'md' | 'lg';
}

const SIZE = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
} as const satisfies Record<'sm' | 'md' | 'lg', string>;

Dialog.Content = function DialogContent({ children, className, size = 'md' }: DialogContentProps) {
  return (
    <BaseDialog.Portal>
      <BaseDialog.Backdrop
        className={cn(
          'fixed inset-0 z-40 bg-black/25 backdrop-blur-[3px]',
          'ease-out-quart transition-opacity duration-300',
          'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
          'motion-reduce:transition-none',
        )}
      />
      <BaseDialog.Viewport className="fixed inset-0 z-50 grid place-items-center overflow-y-auto overscroll-contain p-4">
        <BaseDialog.Popup
          className={cn(
            'rounded-sheet text-fg relative w-full outline-none',
            'bg-overlay shadow-modal',
            'ease-spring transition-[transform,opacity] duration-300',
            // Enter scales up slightly; exit is quicker than the entrance.
            'data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
            'data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
            'motion-reduce:transition-none',
            SIZE[size],
            className,
          )}
        >
          {children}
        </BaseDialog.Popup>
      </BaseDialog.Viewport>
    </BaseDialog.Portal>
  );
};

Dialog.Header = function DialogHeader({
  title,
  description,
  showClose = true,
}: {
  title: ReactNode;
  description?: ReactNode;
  showClose?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3 px-6 pt-6">
      <div className="min-w-0 flex-1">
        <BaseDialog.Title className="text-[19px] font-semibold tracking-[-0.017em]">
          {title}
        </BaseDialog.Title>
        {description && (
          <BaseDialog.Description className="text-fg-muted mt-1 text-[14px]">
            {description}
          </BaseDialog.Description>
        )}
      </div>
      {showClose && (
        <BaseDialog.Close
          render={
            <IconButton
              label={t.common.close}
              icon={<X />}
              variant="ghost"
              size="icon-sm"
              showTooltip={false}
            />
          }
        />
      )}
    </div>
  );
};

Dialog.Body = function DialogBody({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('px-6 py-5', className)} {...props} />;
};

Dialog.Footer = function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div className={cn('flex items-center justify-end gap-2 px-6 pb-6', className)} {...props} />
  );
};
